import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllReports } from "../services/dbService";
import {
  User,
  Calendar,
  ArrowRight,
  Loader,
  LayoutDashboard,
  Activity,
  CheckCircle2,
  Clock,
  // Settings,
  // Users,
  LogOut,
  ChevronDown,
  Trash2,
  Menu,
  X,
} from "lucide-react";

const AdminDashboard = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const data = await getAllReports();

        // Filter out locally deleted reports
        const deletedIds = JSON.parse(
          localStorage.getItem("deleted_reports") || "[]"
        );
        const activeReports = (data || []).filter(
          (report) => !deletedIds.includes(report.id)
        );

        setReports(activeReports);
      } catch (error) {
        console.error("Failed to load reports", error);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, []);

  const handleViewReport = (report) => {
    navigate("/admin/report", { state: { patientData: report } });
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to sign out?")) {
      localStorage.removeItem("isAdminAuthenticated");
      navigate("/login");
    }
  };

  // New Delete Handler
  const handleDeleteReport = (e, reportId) => {
    e.stopPropagation(); // Prevent triggering the row click
    if (
      window.confirm(
        "Are you sure you want to delete this report? This action cannot be undone."
      )
    ) {
      // Persist deletion to local storage
      const deletedIds = JSON.parse(
        localStorage.getItem("deleted_reports") || "[]"
      );
      localStorage.setItem(
        "deleted_reports",
        JSON.stringify([...deletedIds, reportId])
      );

      // Update UI state
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    }
  };

  // Filter logic
  const pendingReports = reports.filter((r) => r.status === "new" || !r.status);
  const reviewedReports = reports.filter((r) => r.status === "reviewed");
  const displayedReports =
    activeTab === "pending" ? pendingReports : reviewedReports;

  return (
    <div className="page admin-dashboard-page">
      {/* --- MOBILE OVERLAY --- */}
      {isMobileMenuOpen && (
        <div
          className="mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* --- SIDEBAR MENU --- */}
      <aside className={`admin-sidebar ${isMobileMenuOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <LayoutDashboard size={28} className="header-icon" />
          <div className="title-section">
            <h1>PhysioAdmin</h1>
            <p>Clinical Portal</p>
          </div>
          <button
            className="mobile-close-btn"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <X size={24} />
          </button>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-group">
            <label>Assessment Queue</label>
            <button
              className={`nav-item ${activeTab === "pending" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("pending");
                setIsMobileMenuOpen(false);
              }}
            >
              <Clock size={20} />
              <span>Pending</span>
              <span className="count-badge">{pendingReports.length}</span>
            </button>
            <button
              className={`nav-item ${activeTab === "reviewed" ? "active" : ""}`}
              onClick={() => {
                setActiveTab("reviewed");
                setIsMobileMenuOpen(false);
              }}
            >
              <CheckCircle2 size={20} />
              <span>Reviewed</span>
              <span className="count-badge">{reviewedReports.length}</span>
            </button>
          </div>

          {/* <div className="nav-group">
            <label>Management</label>
            <button className="nav-item disabled">
              <Users size={20} />
              <span>User List</span>
            </button>
            <button className="nav-item disabled">
              <Settings size={20} />
              <span>System Settings</span>
            </button>
          </div> */}
        </nav>

        <div className="sidebar-footer">
          <div className="stat-card">
            <span className="stat-label">Total Reports</span>
            <span className="stat-value">{reports.length}</span>
          </div>
        </div>
      </aside>

      {/* --- MAIN CONTENT AREA --- */}
      <main className="dashboard-main-content">
        <header className="content-header">
          <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              className="mobile-menu-btn"
              onClick={() => setIsMobileMenuOpen(true)}
              style={{
                display: 'none', // Hidden by default, shown in mobile/tablet via CSS
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#64748b'
              }}
            >
              <Menu size={24} />
            </button>
            <h2>
              {activeTab === "pending"
                ? "Pending Assessments"
                : "Reviewed Records"}
            </h2>
          </div>

          {/* Profile Dropdown Area */}
          <div
            className="user-profile-wrapper"
            onClick={() => setIsProfileOpen(!isProfileOpen)}
          >
            <div className="user-profile-mini">
              <div className="avatar-small">
                <User size={16} />
              </div>
              <span>Dr. Administrator</span>
              <ChevronDown
                size={14}
                className={`dropdown-arrow ${isProfileOpen ? "open" : ""}`}
              />
            </div>

            {/* Dropdown Menu */}
            {isProfileOpen && (
              <div className="profile-dropdown">
                <div className="dropdown-header">
                  <span className="name">Dr. Administrator</span>
                  <span className="role">Super Admin</span>
                </div>
                <div className="dropdown-divider"></div>
                <button onClick={handleLogout} className="dropdown-item danger">
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="content-scroll-area">
          {loading ? (
            <div className="loading-state">
              <Loader className="spin" size={40} />
              <p>Loading clinical database...</p>
            </div>
          ) : displayedReports.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <Activity size={48} />
              </div>
              <h3>No {activeTab} reports</h3>
              <p>
                {activeTab === "pending"
                  ? "Great job! You've cleared all pending assessments."
                  : "Reports marked as reviewed will appear here."}
              </p>
            </div>
          ) : (
            <div className="reports-list">
              {displayedReports.map((report) => (
                <div
                  key={report.id}
                  className={`patient-row status-${report.status || "new"}`}
                  onClick={() => handleViewReport(report)}
                >
                  <div className="patient-info">
                    <div className="avatar">
                      <User size={24} />
                    </div>
                    <div className="details">
                      <h3 className="patient-email">
                        {report.email || "Anonymous Patient"}
                      </h3>
                      <div className="meta-info">
                        <span className="demographics">
                          {report.gender || "Unknown"} • {report.age || "N/A"}
                        </span>
                        <span className="timestamp">
                          <Calendar size={14} />
                          {report.createdAt?.seconds
                            ? new Date(
                              report.createdAt.seconds * 1000
                            ).toLocaleDateString()
                            : "Recent"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="report-meta">
                    <div className="location-tag">
                      {report.painLocation || "General"} Pain
                    </div>
                    <div
                      className={`intensity-badge level-${Math.ceil(
                        report.painIntensity / 2
                      )}`}
                    >
                      Pain: {report.painIntensity}/10
                    </div>
                  </div>

                  {/* Action Area: Delete + View */}
                  <div
                    className="action-area"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      marginLeft: "2rem",
                    }}
                  >
                    <button
                      className="delete-action-btn"
                      onClick={(e) => handleDeleteReport(e, report.id)}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#cbd5e1",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        transition: "color 0.2s",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "#ef4444")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "#cbd5e1")
                      }
                      title="Delete Report"
                    >
                      <Trash2 size={18} />
                    </button>
                    <div className="action-icon">
                      <ArrowRight size={20} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
